// src/pages/reports/ReportingDashboard.js

import { useEffect, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import { Link } from "react-router-dom";
import { receivedValueOf, receivedAtOf } from "../../utils/poReceived";

export default function ReportingDashboard() {
  const user      = JSON.parse(localStorage.getItem("user") || "{}");
  const companyId = user.company_id || "";

  const [stats,   setStats]   = useState({
    inventoryValue: 0, monthlyPurchase: 0,
    monthlyExpense: 0, monthlyBilling:  0, netProfit: 0,
  });
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
useEffect(() => { if (companyId) fetchReportStats(); }, [companyId]);

  const fetchReportStats = async () => {
    try {
      setLoading(true);
      const now          = new Date();
      const currentMonth = now.getMonth();
      const currentYear  = now.getFullYear();

      const inThisMonth = (ts) => {
        if (!ts?.seconds) return false;
        const d = new Date(ts.seconds * 1000);
        return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
      };

      // ── Inventory value ─────────────────────────────────
      // inventory_summary has qty field; rate not available without GRN
      const invSnap = await getDocs(
        query(collection(db, "inventory_summary"), where("company_id", "==", companyId))
      );
      let inventoryValue = 0;
      invSnap.forEach(d => {
        const item = d.data();
        // rate not in items collection — use 0 until GRN data available
        inventoryValue += Number(item.qty || 0) * Number(item.last_rate || item.rate || 0);
      });

      // ── Monthly purchases (POs received) ────────────────
      // MED-34: only RECEIVED POs, in the month the goods came.
      const poSnap = await getDocs(
        query(collection(db, "purchase_orders"), where("company_id", "==", companyId))
      );
      let monthlyPurchase = 0;
      poSnap.forEach(d => {
        const data = d.data();
        if (inThisMonth(receivedAtOf(data)))
          monthlyPurchase += receivedValueOf(data);
      });

      // ── Monthly expenses + transport ─────────────────────
      const [expSnap, transSnap] = await Promise.all([
        getDocs(query(collection(db, "financial_transactions"),
          where("company_id", "==", companyId), where("type", "==", "expense"))),
        getDocs(query(collection(db, "financial_transactions"),
          where("company_id", "==", companyId), where("type", "==", "transport"))),
      ]);
      let monthlyExpense = 0;
      [...expSnap.docs, ...transSnap.docs].forEach(d => {
        const data = d.data();
        if (inThisMonth(data.created_at)) monthlyExpense += Number(data.amount || 0);
      });

      // ── Monthly billing ──────────────────────────────────
      const invBillSnap = await getDocs(
        query(collection(db, "invoices"), where("company_id", "==", companyId))
      );
      let monthlyBilling = 0;
      invBillSnap.forEach(d => {
        const data = d.data();
        if (inThisMonth(data.invoice_date)) monthlyBilling += Number(data.total_amount || 0);
      });

      setStats({
        inventoryValue, monthlyPurchase, monthlyExpense,
        monthlyBilling, netProfit: monthlyBilling - monthlyExpense - monthlyPurchase,
      });
    } catch (err) {
      console.error("Reporting dashboard error:", err);
    } finally { setLoading(false); }
  };

  const role = user?.role || "";

  const MODULES = [
    { to: "/reports/financial", title: "Financial Reports",  desc: "Revenue, expenses, vendor dues, profitability", icon: "💰" },
    ...(role === "owner" ? [
      { to: "/reports/inventory", title: "Inventory Reports", desc: "Stock levels, inventory value, low stock alerts", icon: "📦" },
      { to: "/reports/projects",  title: "Project Reports",  desc: "Project costing and performance analytics",      icon: "📋" },
    ] : []),
  ];

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold">Reports Dashboard</h1>

      {/* SUMMARY CARDS */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard title="Inventory Value"   value={`₹${stats.inventoryValue.toLocaleString()}`}  color="blue" />
        <StatCard title="Monthly Purchase"  value={`₹${stats.monthlyPurchase.toLocaleString()}`} color="purple" />
        <StatCard title="Monthly Expense"   value={`₹${stats.monthlyExpense.toLocaleString()}`}  color="red" />
        <StatCard title="Monthly Billing"   value={`₹${stats.monthlyBilling.toLocaleString()}`}  color="green" />
        <StatCard title="Net Profit"
          value={`₹${stats.netProfit.toLocaleString()}`}
          color={stats.netProfit >= 0 ? "green" : "red"} />
      </div>

      {/* MODULE LINKS */}
      <div className="bg-white rounded-xl shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Reports Modules</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {MODULES.map(m => (
            <Link key={m.to} to={m.to}
              className="p-4 rounded-xl border border-slate-200 hover:bg-slate-50 hover:border-slate-300 transition">
              <div className="text-2xl mb-2">{m.icon}</div>
              <h3 className="font-semibold text-slate-800">{m.title}</h3>
              <p className="text-sm text-slate-500 mt-1">{m.desc}</p>
            </Link>
          ))}
        </div>
      </div>

      {/* BUSINESS SUMMARY */}
      <div className="bg-white rounded-xl shadow p-6">
        <h2 className="text-lg font-semibold mb-4">This Month Summary</h2>
        {loading ? <p className="text-slate-400 text-sm">Loading...</p> : (
          <div className="space-y-3 text-sm">
            <Row label="Total Inventory Asset"  value={`₹${stats.inventoryValue.toLocaleString()}`} />
            <Row label="Revenue (Invoices)"     value={`₹${stats.monthlyBilling.toLocaleString()}`}  green />
            <Row label="Expenses + Transport"   value={`₹${stats.monthlyExpense.toLocaleString()}`}  red />
            <Row label="Purchase Orders"        value={`₹${stats.monthlyPurchase.toLocaleString()}`} red />
            <div className="border-t pt-3">
              <Row label="Estimated Net P&L"
                value={`₹${stats.netProfit.toLocaleString()}`}
                green={stats.netProfit >= 0} red={stats.netProfit < 0} bold />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, green, red, bold }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-slate-500">{label}</span>
      <span className={`font-${bold ? "bold" : "semibold"} ${green ? "text-green-600" : red ? "text-red-600" : "text-slate-800"}`}>
        {value}
      </span>
    </div>
  );
}

function StatCard({ title, value, color = "blue" }) {
  const colors = {
    blue:   "bg-blue-50   border-blue-200",
    green:  "bg-green-50  border-green-200",
    red:    "bg-red-50    border-red-200",
    purple: "bg-purple-50 border-purple-200",
  };
  return (
    <div className={`rounded-xl border p-4 ${colors[color] || colors.blue}`}>
      <p className="text-xs text-slate-500 uppercase tracking-wide">{title}</p>
      <h3 className="text-xl font-bold mt-1">{value}</h3>
    </div>
  );
}
